/* eslint-disable no-shadow */
import { sessionStorage } from '@/app/shared/services/sessionStorage.service';
import { authApi } from '@/app/shared/services/apis/authApi.service';
import { apiClient } from '@/app/core/services/apiClient.service';
import {
    CODE_VERIFIER,
    REFRESH_TOKEN_KEY,
    SSO_CODE,
    TOKEN_EXPIRE_AT_KEY,
    TOKEN_EXPIRE_IN_KEY,
    TOKEN_KEY,
} from '@/app/shared/constants/session.constants';
import dayjs from 'dayjs';
import router from '@/app/router';
import { echoService } from '@/app/core/services/echo.service';
import { OAUTH_CLIENT_ID } from '@/app/core/common/config';

const initialState = {
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
    isInitialized: false,
    redirectUrl: null,
    ssoCode: null,
    codeVerifier: null,
};

const state = () => ({ ...initialState });

const getters = {
    token: (state) => {
        return state.accessToken;
    },
    sessionExpiresAt: (state) => {
        return state.expiresAt;
    },
    isLoggedIn: (state, getters) => {
        return !!(
            getters.token &&
            getters.sessionExpiresAt &&
            dayjs().isBefore(dayjs(getters.sessionExpiresAt))
        );
    },
};

const actions = {
    async login(context) {
        const response = await authApi.login();

        if (response.redirect_url) {
            context.commit('setRedirectUrl', response.redirect_url);
            context.commit('setCodeVerifier', response.code_verifier);
        } else {
            await context.dispatch('changeTokenData', response);
            await context.dispatch('User/fetchUserData', undefined, { root: true });
        }

        return response;
    },
    async callback(context, data) {
        try {
            context.commit('setSsoCode', data.code);

            const code_verifier = sessionStorage.get('code_verifier') ?? null;

            const response = await authApi.getToken({
                grant_type: 'fdid',
                code: data.code,
                code_verifier,
                client_id: OAUTH_CLIENT_ID,
            });

            await context.dispatch('changeTokenData', response);
            await context.dispatch('Routing/setCurrentMainEventId', null, { root: true });
            await context.dispatch('User/fetchUserData', undefined, { root: true });
            return response;
        } catch (error) {
            await context.dispatch('clearSession');
            throw error;
        }
    },
    async signOut(context) {
        try {
            await authApi.signOut();
        } finally {
            await context.dispatch('clearSession');
            await context.dispatch('redirectToLogin');
        }
    },
    async changeTokenData(context, data) {
        sessionStorage.set(TOKEN_KEY, data.access_token);
        sessionStorage.set(REFRESH_TOKEN_KEY, data.refresh_token);
        sessionStorage.set(TOKEN_EXPIRE_AT_KEY, data.expires_at);

        if (data.access_token && data.refresh_token && data.expires_at) {
            await apiClient.setAuthTokenHeader(data.access_token);
            echoService.updateAuthorizationToken(data.access_token);
            context.commit('setToken', data);
            context.commit('updateExpirationTime', data.expires_at);
        } else {
            // TODO: callback not working with 2 lines below (code_verifier is deleted from session before being passed to the backend):
            // context.dispatch('clearSession');
            // throw new Error('No access token provided!');
        }
    },
    redirectToLogin() {
        router.replace({ name: 'Login' });
    },
    async renewSession({ state, dispatch }, { showToast } = {}) {
        if (!state.refreshToken) {
            await dispatch('clearSession');
            return;
        }
        try {
            const response = await authApi.refreshSession(state.refreshToken, {
                showErrorToast: showToast,
            });
            await dispatch('changeTokenData', response);
        } catch {
            await dispatch('clearSession');
        }
    },
    clearSession(context) {
        sessionStorage.clearAll();
        context.commit('setSessionCleared');
    },
    async initialize(context) {
        const token = {
            access_token: sessionStorage.get(TOKEN_KEY) ?? null,
            refresh_token: sessionStorage.get(REFRESH_TOKEN_KEY) ?? null,
            expires_at: sessionStorage.get(TOKEN_EXPIRE_AT_KEY) ?? null,
        };

        await context.dispatch('changeTokenData', token);

        if (context.getters.token) {
            await apiClient.setAuthTokenHeader(context.getters.token);
            await context.dispatch('User/fetchUserData', undefined, { root: true });
        }

        context.commit('setInitialized');
    },
};

const mutations = {
    setRedirectUrl(state, url) {
        state.redirectUrl = url;
    },
    setInitialized(state) {
        state.isInitialized = true;
    },
    setSsoCode(state, data) {
        state.ssoCode = data.code;
        sessionStorage.set(SSO_CODE, data.code);
    },
    setCodeVerifier(state, code) {
        state.codeVerifier = code;
        sessionStorage.set(CODE_VERIFIER, code);
    },
    setToken(state, token) {
        state.accessToken = token.access_token;
        sessionStorage.set(TOKEN_KEY, token.access_token);
        state.refreshToken = token.refresh_token;
        sessionStorage.set(REFRESH_TOKEN_KEY, token.refresh_token);
        state.expiresAt = token.expires_at;
        sessionStorage.set(TOKEN_EXPIRE_AT_KEY, token.expires_at);
    },
    updateExpirationTime(state, expirationDate) {
        sessionStorage.set(TOKEN_EXPIRE_IN_KEY, expirationDate);
        state.expiresAt = expirationDate;
    },
    setSessionCleared(state) {
        for (const f in state) {
            state[f] = initialState[f];
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
